// Partner portal English translations.
// Canonical schema: FR must match every key exactly.

export const en = {
  common: {
    loading: 'Loading...',
    saving: 'Saving...',
    saved: 'Saved.',
    save: 'Save',
    cancel: 'Cancel',
    back: 'Back',
    close: 'Close',
    menu: 'Menu',
    signOut: 'Sign out',
    error: 'Error',
    networkError: 'Network error. Check your connection and try again.',
    httpError: 'Server error ({status}). Try again or contact partners@ailysagency.ca.',
    contactSupport: 'Contact partners@ailysagency.ca',
    poweredBy: 'Powered by AiLys',
    notSet: 'Not set',
    notOnFile: 'Not on file',
    enabled: 'Enabled',
    disabled: 'Disabled',
    yes: 'Yes',
    no: 'No',
  },

  nav: {
    brand: 'AiLys Partner',
    dashboard: 'Dashboard',
    clients: 'Clients',
    commissions: 'Commissions',
    team: 'Team',
    training: 'Training',
    whiteLabel: 'White label',
    settings: 'Settings',
    profile: 'Profile',
  },

  login: {
    pageTitle: 'Login - AiLys Partner Portal',
    brand: 'AiLys Partner Portal',
    heading: 'Sign in.',
    subtitle: 'Use the credentials AiLys sent when you were activated as a Tier 1 pilot. Two-factor authentication is mandatory; have your authenticator app ready.',
    ssoButton: 'Continue with single sign-on',
    newPartner: 'New partner agency?',
    applyLink: 'Apply via the partner program waitlist',
    lostAccess: 'Lost access?',
    lostAccessEmail: 'partners@ailysagency.ca',
  },

  dashboard: {
    wrongPortalTitle: 'Wrong portal',
    wrongPortalMsg: 'You are signed in as a client. The partner portal at',
    wrongPortalMsg2: 'is for partner agencies who resell AiLys. To manage your own subscription, visit the main AiLys client dashboard.',
    partnerOverview: 'Partner overview',
    activeClients: 'Active clients',
    activeClientsDesc: 'Attributed to your organization',
    ytdCommissions: 'YTD commissions',
    ytdCommissionsDesc: 'T4A-eligible total (CAD)',
    tier: 'Tier',
    tierDesc: 'Qualification level',
    certification: 'Certification',
    quickLinks: 'Quick links',
    linkClients: 'Your attributed clients',
    linkCommissions: 'Commission statements',
    linkSettings: 'Account settings',
    linkHelp: 'Help center',
    staffOverview: 'AiLys staff overview',
    staffOverviewMsg: 'Internal view of the partner channel. Pilot list + recent attribution events ship in a follow-up chunk. Full admin access at',
    adminOverview: 'AiLys admin overview',
    adminOverviewMsg: 'Full admin access to partner channel data lives at',
    tierPending: 'Pending',
    tierStarter: 'Starter',
    tierSilver: 'Silver',
    tierGold: 'Gold',
    tierElite: 'Elite',
    certInTraining: 'In training',
    certActive: 'Active',
    certExpired: 'Expired',
    certRevoked: 'Revoked',
    certRenewRequired: 'Renewal required',
    certContactSupport: 'Contact AiLys support',
    certCompleteTraining: 'Complete training to certify',
    certRecertifyDays: 'Recertify in {days} days',
    certRenewalOverdue: 'Renewal overdue',
    certRecertifyBefore: 'Recertify before 365 days',
  },

  clients: {
    title: 'Your clients',
    subtitle: 'Businesses you closed for AiLys. Search by tenant ID or tier. Empty state means no attributions yet; first close registers from the founder onboarding playbook Step 10.',
    searchPlaceholder: 'Search by tenant ID or tier...',
    errorMsg: 'Failed to load clients ({error}). Try again or contact partners@ailysagency.ca.',
    emptyTitle: 'No attributed clients',
    emptyDesc: 'No attributed clients yet. Your first close lands here once the AiLys founder registers it after you report the deal.',
    colClosed: 'Closed',
    colTenant: 'Tenant',
    colTier: 'Tier',
    colMrr: 'MRR',
    colStatus: 'Status',
    colCsat: 'CSAT',
    colLoi25: 'Loi 25',
    statusActive: 'ACTIVE',
    statusStaff: 'staff',
    loi25Missing: 'MISSING',
    loi25OnFile: 'on file',
  },

  clientDetail: {
    backLink: 'Back to clients',
    notFoundTitle: 'Not found',
    notFoundMsg: 'No client with that ID is attributed to your organization. Either the ID is wrong, or this client belongs to another partner. If you believe this is an error, contact partners@ailysagency.ca.',
    title: 'Client detail',
    closedOn: 'Closed',
    tierAtClose: 'Tier at close',
    initialMrr: 'Initial MRR',
    currentMrr: 'Current MRR',
    status: 'Status',
    csat: 'CSAT',
    csatNone: 'No survey yet',
    loi25Legacy: 'Loi 25 consent (legacy proof)',
    loi25Missing: 'MISSING - request signed Schedule B from client',
    consentLedgerTitle: 'Loi 25 consent ledger',
    consentLedgerDesc: 'Multi-level consent per ADR-0019. Active partner-views consent is required before this client\'s data is visible to your agency. Withdraw anytime; audit trail preserved.',
    consentLoading: 'Loading consent ledger...',
    consentActive: 'ACTIVE consent ({scopes})',
    consentNone: 'NO active consent - capture below before accessing data',
    consentCapture: 'Capture Schedule B consent',
    consentHistory: 'History ({count})',
    consentWithdrawn: 'withdrawn {date}',
    consentWithdraw: 'Withdraw',
    consentCaptured: 'Consent captured. Schedule B record stored.',
    consentWithdrawnSuccess: 'Consent withdrawn. Audit trail preserved.',
  },

  commissions: {
    title: 'Commissions',
    subtitle: 'Year-to-date T4A-eligible total + monthly statement list. Statements are paid the 1st of the month after the period closes (Tier 1 manual MVP via Interac e-Transfer; Stripe Connect Express direct deposit ships in Phase 6.2).',
    errorMsg: 'Failed to load commissions ({error}). Try again or contact partners@ailysagency.ca.',
    ytdTitle: 'YTD T4A-eligible total (CAD)',
    ytdDesc: 'Sum of net commissions paid in {year} flagged T4A-eligible. If this total reaches $500 CAD by year-end, AiLys issues a T4A slip in February.',
    colPeriod: 'Period',
    colAttributions: 'Attributions',
    colGross: 'Gross',
    colAdjust: 'Adjust.',
    colNet: 'Net',
    colStatus: 'Status',
    colPaid: 'Paid',
    colT4a: 'T4A',
    periodRange: '{start} to {end}',
    noPaid: '-',
  },

  team: {
    title: 'Team',
    lockedTitle: 'Silver tier required',
    lockedDesc: 'Sub-agent hiring unlocks at Tier 2 Silver. Tier 1 Starter partners operate solo. Reach 30 qualifying closes, 80% retention, and $400 average MRR to qualify.',
    backLink: 'Back to dashboard',
    subtitle: 'Manage your sub-agents and client assignments',
    inviteBtn: 'Invite agent',
    inviteTitle: 'Invite a sub-agent',
    inviteSuccess: 'Invite created. Share this link with the agent:',
    fieldName: 'Name',
    fieldNamePlaceholder: 'Agent name',
    fieldEmail: 'Email',
    fieldEmailPlaceholder: 'agent@example.com',
    fieldClassification: 'Classification',
    classOther: 'Other',
    classW2: 'W-2 Employee',
    classT4a: 'T4A Contractor',
    fieldCommission: 'Commission rate (%)',
    sendInvite: 'Send invite',
    loadingTeam: 'Loading team...',
    emptyTitle: 'No agents yet',
    emptyDesc: 'Click "Invite agent" to add your first sub-agent.',
    activeAgents: 'Active agents ({count})',
    joined: 'Joined {date}',
    commission: 'Commission',
    assignedClients: 'Clients',
    shadowCopilotSolo: 'Shadow / Copilot / Solo',
    terminate: 'Terminate',
    terminatedSection: 'Terminated ({count})',
    terminatedOn: 'Terminated {date}',
    inviteFailed: 'Failed to send invite. Try again.',
    terminateFailed: 'Failed to terminate agent. Try again.',
    commissionUpdateFailed: 'Failed to update commission. Try again.',
  },

  training: {
    title: 'AiLys University',
    subtitle: 'Training',
    backLink: 'Back to Dashboard',
    tabCourses: 'Courses ({count})',
    tabCertifications: 'Certifications ({count})',
    noCourses: 'No courses available yet. Check back soon.',
    noCerts: 'No certifications yet. Complete a course to earn your first certification.',
    hours: '~{hours}h',
    modules: '{count} modules',
    progress: '{pct}%',
    moduleDone: '{done}/{total} done',
    duration: '{duration}min',
    optional: 'optional',
    attempts: '{count} attempt(s)',
    issued: 'Issued: {date}',
    expires: 'Expires: {date}',
    daysRemaining: '{days}d remaining',
    categoryProduct: 'product',
    categorySales: 'sales',
    categoryTechnical: 'technical',
    categoryCompliance: 'compliance',
    loadingTraining: 'Loading training data...',
  },

  whiteLabel: {
    title: 'White-Label Branding',
    lockedDesc: 'White-label branding is available for Gold and Elite tier partners. Your client-facing materials (reports, emails, landing pages) will display your branding instead of AiLys branding.',
    lockedAction: 'Contact your strategist or reach Gold tier to unlock this feature.',
    subtitle: 'Customize how your brand appears on client-facing materials.',
    fieldLogoUrl: 'Logo URL (https only)',
    fieldLogoPlaceholder: 'https://your-domain.com/logo.png',
    fieldPrimaryColor: 'Primary color',
    fieldSecondaryColor: 'Secondary color',
    fieldCompanyName: 'Company name (shown on reports)',
    fieldCompanyPlaceholder: 'Your Agency Name',
    fieldFooterText: 'Footer text (shown at bottom of reports)',
    fieldFooterPlaceholder: 'Powered by Your Agency',
    fieldEmailFromName: 'Email "From" name',
    fieldEmailFromPlaceholder: 'Your Agency',
    previewTitle: 'Preview',
    previewFooter: 'Footer text will appear here on client reports.',
    saveBtn: 'Save branding',
    saved: 'Branding saved.',
  },

  settings: {
    title: 'Settings',
    errorMsg: 'Failed to load settings ({error}).',
    orgSection: 'Organization',
    orgTier: 'Tier',
    orgCert: 'Cert',
    orgCertExpires: 'Cert expires',
    legalEntity: 'Legal entity (BN or SIN)',
    legalEntityReadonly: 'Read-only. To change your legal entity (e.g., REQ rebranding or BN reissue), email partners@ailysagency.ca with documentation. AiLys staff updates the record after verification.',
    addressSection: 'Mailing address (T4A delivery)',
    addressDesc: 'Used for postal T4A delivery if you have not opted into email delivery. Must match your registered legal address.',
    fieldStreet: 'Street address',
    fieldApt: 'Apt / Suite / Unit (optional)',
    fieldCity: 'City',
    fieldProvince: 'Province',
    fieldPostal: 'Postal code',
    postalPlaceholder: 'A1A 1A1',
    saveAddress: 'Save address',
    twoFaSection: 'Two-factor authentication',
    twoFaDesc: 'Mandatory for partner_owner. Manage your TOTP authenticator or recovery codes via the AiLys identity provider:',
    twoFaLink: 'Manage 2FA settings',
    complianceSection: 'Compliance status',
    dpaSigned: 'DPA signed:',
    msaSigned: 'MSA signed:',
    notSigned: 'NOT SIGNED',
  },

  profile: {
    title: 'Public Profile',
    subtitle: 'Customize your co-branded landing page at ailysagency.ca/partners/your-slug',
    loadingProfile: 'Loading profile...',
    fieldDisplayName: 'Display name',
    fieldDisplayNamePlaceholder: 'Your name as shown publicly',
    fieldHeadline: 'Headline',
    fieldHeadlinePlaceholder: 'e.g. Local SEO specialist for restaurants',
    fieldBio: 'Bio',
    fieldBioPlaceholder: 'Tell potential clients about yourself...',
    fieldPhotoUrl: 'Photo URL',
    fieldPhotoUrlPlaceholder: 'https://...',
    fieldCity: 'City',
    fieldCityPlaceholder: 'Montreal',
    fieldCountry: 'Country',
    fieldCountryPlaceholder: 'CA',
    fieldSpecialties: 'Specialties (comma-separated)',
    fieldSpecialtiesPlaceholder: 'restaurants, hotels, dental clinics',
    fieldSlug: 'Public slug',
    slugPrefix: 'ailysagency.ca/partners/',
    slugPlaceholder: 'your-name',
    fieldContactEmail: 'Contact email',
    fieldContactEmailPlaceholder: 'you@example.com',
    fieldLinkedIn: 'LinkedIn URL',
    fieldLinkedInPlaceholder: 'https://linkedin.com/in/your-profile',
    enableLanding: 'Enable public landing page',
    enableLandingDesc: 'When enabled, your profile is visible at ailysagency.ca/partners/{slug}',
    saveProfile: 'Save profile',
    linksSection: 'Your links',
    linkLandingPage: 'Landing page:',
    linkSigImage: 'Email signature:',
    copySigUrl: 'Copy signature image URL',
  },

  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all as read',
    empty: 'No notifications yet.',
  },

  session: {
    expiringTitle: 'Session expiring',
    expiringMsg: 'Your session expires in {seconds}s.',
    extend: 'Extend session',
    expired: 'Session expired. Please sign in again.',
  },
} as const;

// Deep-widen: every leaf becomes `string` so FR translations compile.
type DeepString<T> = { [K in keyof T]: T[K] extends string ? string : DeepString<T[K]> };

export type TranslationKeys = DeepString<typeof en>;
